/**
 * KYC Status Manager - Utility functions for managing KYC status persistence
 */

export const KYCStatusManager = {
  /**
   * Save KYC status to localStorage
   */
  saveKYCStatus: (merchantId, statusData) => {
    try {
      const timestamp = new Date().toISOString();
      const dataWithTimestamp = {
        ...statusData,
        lastUpdated: timestamp,
        merchantId: merchantId
      };

      // Save general KYC status
      localStorage.setItem('kycStatus', JSON.stringify(dataWithTimestamp));
      
      // Save merchant-specific status
      if (merchantId) {
        localStorage.setItem(`kyc_status_${merchantId}`, JSON.stringify(dataWithTimestamp));
      }

      // Set completion flag
      if (statusData.status === 'APPROVED' || statusData.status === 'PENDING_REVIEW') {
        localStorage.setItem('kycVerificationComplete', 'true');
      }

      return true;
    } catch (error) {
      console.error('Error saving KYC status:', error);
      return false;
    }
  },

  /**
   * Get KYC status from localStorage
   */
  getKYCStatus: (merchantId = null) => {
    try {
      // Try merchant-specific status first
      if (merchantId) {
        const merchantStatus = localStorage.getItem(`kyc_status_${merchantId}`);
        if (merchantStatus) {
          return JSON.parse(merchantStatus);
        }
      }

      // Fall back to general status
      const generalStatus = localStorage.getItem('kycStatus');
      if (generalStatus) {
        return JSON.parse(generalStatus);
      }

      return null;
    } catch (error) {
      console.error('Error getting KYC status:', error);
      return null;
    }
  },

  /**
   * Check if KYC verification is complete
   */
  isKYCComplete: (merchantId = null) => {
    const status = KYCStatusManager.getKYCStatus(merchantId);
    return status && (status.status === 'APPROVED' || status.status === 'PENDING_REVIEW');
  },

  /**
   * Clear KYC status (for testing/reset purposes)
   */
  clearKYCStatus: (merchantId = null) => {
    try {
      localStorage.removeItem('kycStatus');
      localStorage.removeItem('kycVerificationComplete');
      
      if (merchantId) {
        localStorage.removeItem(`kyc_status_${merchantId}`);
      }

      return true;
    } catch (error) {
      console.error('Error clearing KYC status:', error);
      return false;
    }
  },

  /**
   * Update specific fields in KYC status
   */
  updateKYCStatus: (merchantId, updates) => {
    try {
      const currentStatus = KYCStatusManager.getKYCStatus(merchantId);
      if (!currentStatus) {
        return false;
      }

      const updatedStatus = {
        ...currentStatus,
        ...updates,
        lastUpdated: new Date().toISOString()
      };

      return KYCStatusManager.saveKYCStatus(merchantId, updatedStatus);
    } catch (error) {
      console.error('Error updating KYC status:', error);
      return false;
    }
  },

  /**
   * Get KYC completion percentage
   */
  getCompletionPercentage: (merchantId = null) => {
    const status = KYCStatusManager.getKYCStatus(merchantId);
    if (!status) return 0;

    let completed = 0;
    const total = 6;

    if (status.fullName && status.businessName) completed++;
    if (status.panVerified) completed++;
    if (status.registeredAddress && status.city && status.state) completed++;
    if (status.bankVerified) completed++;
    if (status.documentsUploaded) completed++;
    if (status.status === 'PENDING_REVIEW' || status.status === 'APPROVED') completed++;

    return Math.round((completed / total) * 100);
  }
};

export default KYCStatusManager;